// Browser Notifications API utility
// Uses the standard Web Notifications API for push notifications
// Includes fallbacks: tab title flash + audio beep (works even when OS notifications are off)

// ==================== Notification modes ====================

export type NotificationMode =
  | 'combined'      // Combined Mode (git + jira)
  | 'cross_project' // Cross-Project Mode
  | 'jira'          // Jira Activity View
  | 'linear'        // Linear Activity View
  | 'trello'        // Trello Activity View
  | 'indicators';   // Jira Lag/Lead Indicators

export const NOTIFICATION_MODE_LABELS: Record<NotificationMode, string> = {
  combined: 'Combined Mode',
  cross_project: 'Cross-Project Mode',
  jira: 'Jira Activity',
  linear: 'Linear Activity',
  trello: 'Trello Activity',
  indicators: 'Lag / Lead Indicators',
};

// ==================== Settings ====================

const SETTINGS_KEY = 'verigit-notification-settings';

export interface NotificationSettings {
  /** Master switch — when false, all notifications are suppressed */
  enabled: boolean;
  /** Per-mode overrides — when false the mode is muted even if enabled is true */
  modes: Record<NotificationMode, boolean>;
}

const DEFAULT_SETTINGS: NotificationSettings = {
  enabled: true,
  modes: {
    combined: true,
    cross_project: true,
    jira: true,
    linear: true,
    trello: true,
    indicators: true,
  },
};

export function getNotificationSettings(): NotificationSettings {
  try {
    // eslint-disable-next-line n/no-unsupported-features/node-builtins
    const raw = globalThis.localStorage?.getItem(SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Merge with defaults so new modes are always present
      return {
        enabled: parsed.enabled ?? DEFAULT_SETTINGS.enabled,
        modes: { ...DEFAULT_SETTINGS.modes, ...parsed.modes },
      };
    }
  } catch { /* ignore */ }
  return { ...DEFAULT_SETTINGS, modes: { ...DEFAULT_SETTINGS.modes } };
}

export function saveNotificationSettings(settings: NotificationSettings): void {
  // eslint-disable-next-line n/no-unsupported-features/node-builtins
  globalThis.localStorage?.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

// ==================== Permission helpers ====================

let permissionGranted: boolean | null = null;
let swRegistration: ServiceWorkerRegistration | null = null;

/** Register the minimal notification service worker */
async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (swRegistration) return swRegistration;
  // eslint-disable-next-line n/no-unsupported-features/node-builtins
  if (!('serviceWorker' in globalThis.navigator)) return null;
  try {
    // eslint-disable-next-line n/no-unsupported-features/node-builtins
    swRegistration = await globalThis.navigator.serviceWorker.register('/sw-notifications.js', { scope: '/' });
    // Wait for the SW to be ready
    // eslint-disable-next-line n/no-unsupported-features/node-builtins
    await globalThis.navigator.serviceWorker.ready;
    globalThis.console.log('[notifications] Service worker registered');
    return swRegistration;
  } catch (err) {
    globalThis.console.warn('[notifications] Service worker registration failed:', err);
    return null;
  }
}

/** Request notification permission (idempotent — safe to call multiple times) */
export async function requestNotificationPermission(): Promise<boolean> {
  // eslint-disable-next-line n/no-unsupported-features/node-builtins
  if (!('Notification' in globalThis)) return false;

  if ('Notification' in globalThis && globalThis.Notification.permission === 'granted') {
    permissionGranted = true;
    await ensureServiceWorker();
    return true;
  }

  if ('Notification' in globalThis && globalThis.Notification.permission === 'denied') {
    permissionGranted = false;
    return false;
  }

  // Ask the user
  const result = await globalThis.Notification.requestPermission();
  permissionGranted = result === 'granted';
  if (permissionGranted) await ensureServiceWorker();
  return permissionGranted;
}

/** Check if notifications are supported and permitted */
export function canNotify(): boolean {
  if (!('Notification' in window)) return false;
  return Notification.permission === 'granted';
}

/** Get current browser permission state */
export function getPermissionState(): NotificationPermission | 'unsupported' {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

// ==================== Send notification ====================

interface AnalysisNotificationOptions {
  mode?: NotificationMode;
  presetName?: string;
  commitCount: number;
  eventCount: number;
  durationMs: number;
  success: boolean;
  error?: string;
}

/** Send a notification when analysis completes */
export function notifyAnalysisComplete(opts: AnalysisNotificationOptions): void {
  console.log('[notifications] notifyAnalysisComplete called', { mode: opts.mode, success: opts.success });

  if (!('Notification' in window)) {
    console.warn('[notifications] Notification API not available in this browser');
    return;
  }
  console.log('[notifications] Notification.permission =', Notification.permission);

  if (!canNotify()) {
    console.warn('[notifications] canNotify() returned false — permission:', Notification.permission);
    return;
  }

  // Check user settings
  const settings = getNotificationSettings();
  if (!settings.enabled) {
    console.log('[notifications] Suppressed — master toggle is off');
    return;
  }
  if (opts.mode && !settings.modes[opts.mode]) {
    console.log('[notifications] Suppressed — mode', opts.mode, 'is toggled off');
    return;
  }

  const { presetName, commitCount, eventCount, durationMs, success, error } = opts;

  const title = success
    ? `Analysis Complete${presetName ? ` — ${presetName}` : ''}`
    : `Analysis Failed${presetName ? ` — ${presetName}` : ''}`;

  const durationSec = (durationMs / 1000).toFixed(1);
  const body = success
    ? `${commitCount} commits · ${eventCount} events · ${durationSec}s`
    : error || 'Analysis failed';

  // === 1. Browser Notification via Service Worker (reliable in Chrome) ===
  if (canNotify()) {
    showSwNotification(title, body, presetName);
  }

  // === 2. Tab title flash (always works, visible on inactive tabs) ===
  flashTabTitle(title);

  // === 3. Audio beep (works across tabs) ===
  playNotificationSound(success);
}

// ==================== Service Worker notification ====================

async function showSwNotification(title: string, body: string, presetName?: string): Promise<void> {
  const tag = `analysis-${presetName || 'manual'}-${Date.now()}`;
  const options: NotificationOptions = { body, icon: '/verigit.png', tag, silent: false };

  try {
    // Try SW-based notification first (reliable in Chrome)
    const reg = await ensureServiceWorker();
    if (reg) {
      await reg.showNotification(title, options);
      console.log('[notifications] SW notification sent successfully');
      return;
    }
  } catch (err) {
    console.warn('[notifications] SW showNotification failed, trying legacy:', err);
  }

  // Fallback to legacy Notification constructor
  try {
    const n = new Notification(title, options);
    console.log('[notifications] Legacy notification created');
    setTimeout(() => n.close(), 8000);
    n.onclick = () => { window.focus(); n.close(); };
  } catch (err) {
    console.error('[notifications] All notification methods failed:', err);
  }
}

// ==================== Tab title flash ====================

let titleFlashTimer: ReturnType<typeof setInterval> | null = null;
const ORIGINAL_TITLE = typeof document !== 'undefined' ? document.title : '';

function flashTabTitle(message: string): void {
  if (typeof document === 'undefined') return;

  // Clear any existing flash
  if (titleFlashTimer) {
    clearInterval(titleFlashTimer);
    document.title = ORIGINAL_TITLE;
  }

  let show = true;
  titleFlashTimer = setInterval(() => {
    document.title = show ? `🔔 ${message}` : ORIGINAL_TITLE;
    show = !show;
  }, 1000);

  // Stop flashing after 15 seconds
  setTimeout(() => {
    if (titleFlashTimer) {
      clearInterval(titleFlashTimer);
      titleFlashTimer = null;
      document.title = ORIGINAL_TITLE;
    }
  }, 15000);

  // Also stop when the tab becomes visible again
  const handleVisibility = () => {
    if (!document.hidden && titleFlashTimer) {
      clearInterval(titleFlashTimer);
      titleFlashTimer = null;
      document.title = ORIGINAL_TITLE;
      document.removeEventListener('visibilitychange', handleVisibility);
    }
  };
  document.addEventListener('visibilitychange', handleVisibility);
}

// ==================== Audio notification ====================

function playNotificationSound(success: boolean): void {
  try {
    const ctx = new (globalThis.AudioContext || (globalThis as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const now = ctx.currentTime;

    if (success) {
      // Two-tone rising chime
      [440, 660].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.15, now + i * 0.15);
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.15 + 0.3);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + i * 0.15);
        osc.stop(now + i * 0.15 + 0.3);
      });
    } else {
      // Single low tone for failure
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 280;
      gain.gain.setValueAtTime(0.15, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.4);
    }

    // Clean up context after sounds finish
    setTimeout(() => ctx.close(), 1000);
  } catch (err) {
    console.warn('[notifications] Audio notification failed:', err);
  }
}
